// tableSummary.tsx
import React, { useMemo, useState } from 'react';
import './tableSummary.css';

export interface SummaryConfig {
    colName: string; // Column to summarize
    operation: 'sum' | 'avg' | 'count';
    label?: string; // Custom label for display (optional)
    decimals?: number; // Number of decimal places for display
    format?: 'number' | 'currency' | 'percentage'; // Display format
    isVisible?: (userData: any) => boolean; // Function to determine summary visibility
}

interface TableSummaryProps {
    data: Record<string, any>[];
    activeFilters?: Record<string, Set<string>>; // data filters
    summaryConfig: SummaryConfig[];
    userData?: any;
}

const TableSummary: React.FC<TableSummaryProps> = ({
    data,
    activeFilters = {},
    summaryConfig,
    userData,
}) => {
    const [copiedCell, setCopiedCell] = useState<string | null>(null);

    const effectiveSummaryConfigs = useMemo(() => {
        return summaryConfig.filter(col => {
            // If column has isVisible function, use it to determine visibility
            if (col.isVisible) {
                return col.isVisible(userData);
            }
            // If no isVisible function, summary is visible by default
            return true;
        });
    }, [summaryConfig, userData]);

    // Filter data based on active filters
    const filteredData = useMemo(() => {
        let result = data;
        // Apply filters if they exist
        if (Object.keys(activeFilters).length > 0) {
            result = data.filter(row => {
                for (const [columnName, allowedValues] of Object.entries(activeFilters)) {
                    const cellValue = row[columnName];
                    const stringValue = cellValue === null || cellValue === undefined || cellValue === '' 
                        ? '(Empty)' 
                        : String(cellValue);
                    
                    if (!allowedValues.has(stringValue)) {
                        return false;
                    }
                }
                return true;
            });
        }
        
        return result;
    }, [data, activeFilters]);

    // adds a row count as first item in summary
    const extendedSummaryConfig = useMemo(() => {
        const rowCountConfig: SummaryConfig = {
            colName: '_rowCount',
            operation: 'count',
            label: 'Row Count',
            decimals: 0,
            format: 'number'
        };
        return [rowCountConfig, ...effectiveSummaryConfigs];
    }, [effectiveSummaryConfigs]);

    // Calculate summary values
    const summaryValues = useMemo(() => {
        if (!filteredData?.length || !extendedSummaryConfig?.length) return {};
        
        const results: Record<string, any> = {};
        
        extendedSummaryConfig.forEach(config => {
            // Special handling for row count
            if (config.colName === '_rowCount') {
                results[config.colName] = filteredData.length;
                return;
            }

            const columnData = filteredData.map(row => row[config.colName]).filter(val => val !== null && val !== undefined && val !== '');
            const numericData = columnData.map(val => {
                const num = typeof val === 'string' ? parseFloat(val) : Number(val);
                return isNaN(num) ? 0 : num;
            }).filter(num => !isNaN(num));
            
            let result: number | string = 0;
            
            switch (config.operation) {
                case 'sum':
                    result = numericData.reduce((sum, val) => sum + val, 0);
                    break;
                    
                case 'avg':
                    result = numericData.length > 0 ? numericData.reduce((sum, val) => sum + val, 0) / numericData.length : 0;
                    break;
                    
                case 'count':
                    result = columnData.length;
                    break;
                    
                default:
                    result = 0;
            }
            
            results[config.colName] = result;
        });
        
        return results;
    }, [filteredData, extendedSummaryConfig]);

    // Format value for display
    const formatValue = (value: number | string, config: SummaryConfig): string => {
        if (typeof value !== 'number') return String(value);
        
        const decimals = config.decimals ?? 2;
        
        switch (config.format) {
            case 'currency':
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                }).format(value);
            case 'number':
            default:
                if (Number.isInteger(value) && decimals === 0) {
                    return value.toLocaleString();
                }
                return new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                }).format(value);
        }
    };

    // Get display label for a summary operation
    const getOperationLabel = (operation: SummaryConfig['operation']): string => {
        const labels = {
            sum: 'Sum',
            avg: 'Avg',
            count: 'Count',
        };
        return labels[operation] || operation;
    };

    const copyToClipboard = async (value: string, cellId: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedCell(cellId);
            setTimeout(() => setCopiedCell(null), 1200); // Hide after 1.2s
        } catch (error) {
            
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = value;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                document.execCommand('copy');
                setCopiedCell(cellId);
                setTimeout(() => setCopiedCell(null), 1200);
            } catch (fallbackError) {
            }
            document.body.removeChild(textArea);
        }
    };

    if (!data?.length || !extendedSummaryConfig?.length) {
        return null;
    }

    return (
        <div className="tableSummary-mainContainer">
            {extendedSummaryConfig.map((config, index) => {
                const value = summaryValues[config.colName];
                const formattedValue = formatValue(value, config);
                const cellId = `summary-${config.colName}`;

                return (
                    <div
                        key={config.colName}
                        className="tableSummary-cell"
                        onClick={() => copyToClipboard(formattedValue, cellId)}

                    >
                        <div className="tableSummary-content">
                            <span className="tableSummary-label">
                                {config.label || getOperationLabel(config.operation)}
                            </span>
                            <span className="tableSummary-value">
                                {formatValue(value, config)}
                            </span>
                        </div>
                        {copiedCell === cellId && <span className="tableSummary-copied-tooltip">Copied!</span>}
                    </div>
                );
            })}
        </div>
    );
};

export default TableSummary;